import { useEffect, useRef } from 'react';

import { useSidebar } from '@nucleus-commerce/ui/components/sidebar';

const EVENT = 'nucleus:tour-sidebar';

/** Asks the shell's sidebar to open for a step that points into it, and to restore after. */
export function setTourSidebar(open: boolean): void {
  window.dispatchEvent(new CustomEvent<boolean>(EVENT, { detail: open }));
}

/**
 * Lives inside the shell's SidebarProvider (the tour provider sits outside it). Opens the mobile
 * sheet, or expands an icon-only sidebar, while a step needs it, and puts it back afterwards.
 */
export function TourSidebarBridge() {
  const { isMobile, open, setOpen, setOpenMobile } = useSidebar();
  const restore = useRef<(() => void) | null>(null);
  const latest = useRef({ isMobile, open });
  useEffect(() => {
    latest.current = { isMobile, open };
  }, [isMobile, open]);

  useEffect(() => {
    // Two sidebar steps in a row send close then open in one effect flush; the close waits a
    // tick so the open can cancel it instead of the sheet sliding out and back in.
    let closing = 0;
    const onEvent = (event: Event) => {
      const wanted = (event as CustomEvent<boolean>).detail;
      if (wanted) {
        window.clearTimeout(closing);
        if (restore.current) return;
        if (latest.current.isMobile) {
          setOpenMobile(true);
          restore.current = () => setOpenMobile(false);
        } else if (!latest.current.open) {
          setOpen(true);
          restore.current = () => setOpen(false);
        }
        return;
      }
      window.clearTimeout(closing);
      closing = window.setTimeout(() => {
        restore.current?.();
        restore.current = null;
      }, 0);
    };
    window.addEventListener(EVENT, onEvent);
    return () => {
      window.clearTimeout(closing);
      window.removeEventListener(EVENT, onEvent);
    };
  }, [setOpen, setOpenMobile]);

  return null;
}
